import React from 'react';
const { useState, useEffect } = React;
import { cardSt, mutedSt, badgeSt, warnSt, fmtDate, SubTabs } from '../../diagnostic/shared.jsx';
import { BRAND_TEMPLATES, BRAND_FORMATS, DEFAULT_BRAND_PROFILE, SELF_FRAME_DEFAULT, webinarWhen } from '../../../brandConfig.js';
import { ownerCall, Notice } from '../shared.jsx';
import { LoadingState, ErrorState, EmptyState } from '../../ui/States.jsx';
import BrandProfile from './BrandProfile.jsx';
import BrandPhotos from './BrandPhotos.jsx';
import PieceEditor, { newPiece } from './PieceEditor.jsx';
import PiecePreview from './PiecePreview.jsx';
import NotebookEditor, { newNotebookPiece } from './NotebookEditor.jsx';
import PizarraEditor, { newPizarraPiece, PizarraSample } from './PizarraEditor.jsx';
import { PZ_LAYOUTS } from '../../../pizarraSocial.js';
import { NotebookSample } from './NotebookPage.jsx';
import { loadPhoto, exampleImage, brandFontsReady } from './brandClient.js';
import { chipSt } from '../shared.jsx';

// Mi marca: Ale's personal brand (Alejandra Cadario), inside Mi espacio.
// The brand identity is applied ONLY to the pieces drawn on the canvas: these
// screens keep Oike's design. Templates are originals (src/brandConfig.js);
// each piece is a copy that keeps its template and brand versions. Nothing is
// published or scheduled: pieces are exported as PNG and the text is copied.

const STATUS = { draft: 'Borrador', approved: 'Aprobada', archived: 'Archivada' };

// Example content for the catalog (clearly marked as an example): it tells no experience of Ale's
const EXAMPLE = {
  post: { kicker: 'Etapas de cambio', headline: 'Lo que traje en la valija también cuenta', body: 'Antes de pensar en lo que te falta, anotá tres cosas que ya sabés hacer y que viajaron con vos.' },
  carousel: [
    { headline: 'Tres preguntas para empezar de nuevo sin borrar lo vivido', body: '' },
    { headline: '¿Qué te sostuvo hasta acá?', body: 'Nombrá una persona, un hábito o un lugar. Lo que te sostuvo antes puede volver a hacerlo, aunque cambie de forma.' },
    { headline: 'Guardalo para la próxima vez que dudes', body: 'Elegí una de las tres preguntas y respondela esta semana, por escrito.' },
  ],
  youtube: { kicker: 'Nuevo video', headline: 'Migrar sin perderte' },
  article: { kicker: 'Newsletter · Raíces', headline: 'Pertenecer no es llegar: es construir', body: 'Ideas y una acción concreta para quienes atraviesan un cambio de vida o de trabajo.' },
  webinar: { kicker: 'Webinar gratuito', headline: 'Raíces en movimiento', body: 'Una charla franca sobre empezar de nuevo sin borrar lo vivido.', cta: 'Inscribite con el enlace del texto' },
  explainer: { kicker: 'Nuevo video', headline: 'Cómo empezar de nuevo sin borrar lo vivido', highlights: ['vivido'] },
  highlight: { kicker: 'Etapas de cambio', headline: 'Lo que traje en la valija también cuenta', highlights: ['valija', 'cuenta'] },
  idea: { kicker: 'Para pensar', headline: '¿Qué te sostuvo hasta acá?', highlights: ['sostuvo'] },
  blocks: { kicker: 'Alejandra Cadario', headline: 'Cómo pensar diferente', highlights: [] },
  podcast: { kicker: 'Raíces en movimiento', episode: 'Episodio 1', headline: 'Empezar de nuevo en otro país', highlights: ['otro', 'país'] },
};
// The catalog (decided with Ale on 04-11-2026): choose the format → choose the design. Instagram and LinkedIn use the
// Pizarra designs (also YouTube, the TikTok cover and, since 04-11-2026, the LinkedIn article cover); the webinar
// invitation keeps its earlier design until it is replaced («Diseño anterior»). Retired templates are only out of the catalog: saved pieces keep opening as they were.
const CATALOG = [
  { group: 'Instagram', items: [{ id: 'pz_ig_post', label: 'Post', pizarra: 'pz_ig_post' }, { id: 'pz_ig_carousel', label: 'Carrusel', pizarra: 'pz_ig_carousel' }, { id: 'pz_ig_story', label: 'Story', pizarra: 'pz_ig_story' }] },
  { group: 'LinkedIn', items: [{ id: 'pz_li_post', label: 'Post con imagen', pizarra: 'pz_li_post' }, { id: 'pz_li_document', label: 'Documento PDF', pizarra: 'pz_li_document' }, { id: 'pz_li_article', label: 'Portada de artículo', pizarra: 'pz_li_article' }] },
  { group: 'YouTube', items: [{ id: 'pz_youtube', label: 'Miniatura', pizarra: 'pz_youtube' }] },
  // TikTok: the script (to record) and the video cover (an image) are separate pieces; one never replaces the other
  { group: 'TikTok', items: [{ id: 'tiktok', label: 'Guion', script: true }, { id: 'pz_tiktok_cover', label: 'Portada de video', pizarra: 'pz_tiktok_cover' }] },
  { group: 'Documentos', items: [{ id: 'cuaderno', label: 'Cuaderno de trabajo (PDF A4)', notebook: true }] },
  { group: 'Diseño anterior (hasta su reemplazo)', items: [{ id: 'webinar', label: 'Invitación a webinar', old: true }] },
];
const EXAMPLE_WHEN = webinarWhen({ date: '2026-11-12', time: '18:30', timeZone: 'America/Argentina/Buenos_Aires', extraZones: ['America/Mexico_City', 'Europe/Madrid'] });

/** An example piece of an original template, with a photo or only typography. */
function sampleOf(key, format, withPhoto, slide = 0) {
  const p = newPiece(key, null);
  p.format = format;
  const t = (text) => ({ text, origin: 'mine', refs: [] });
  if (BRAND_TEMPLATES[key].collection) {
    const { highlights, ...slots } = EXAMPLE[key];
    for (const [k, v] of Object.entries(slots)) p.content.slots[k] = t(v);
    p.content.style.highlights = highlights;
    p.content.slides = [
      { slots: { headline: t('Nombrá lo que ya sabés hacer'), body: t('Anotá tres cosas que viajaron con vos y que hoy te pueden servir.') } },
      { slots: { headline: t('Elegí una para esta semana'), body: t('Escribila y contale a alguien cómo la vas a usar.') } },
    ];
    p.content.cast = withPhoto ? 'both' : 'self';
    p.content.people = { self: { name: 'Alejandra Cadario', role: 'Anfitriona' }, guest1: { name: 'Nombre Invitada', role: 'Su rol' } };
    p.photos = { self: { photoId: 'example', crop: { x: 0.5, zoom: 1 }, frame: { ...SELF_FRAME_DEFAULT } }, guest1: { photoId: 'example', crop: { x: 0.5, y: 0.4, zoom: 1 } } };
    return p;
  }
  if (key === 'carousel') p.content.slides = EXAMPLE.carousel.map((x, i, all) => ({ kind: i === 0 ? 'cover' : i === all.length - 1 ? 'closing' : 'content', slots: { headline: t(x.headline), body: t(x.body) } }));
  else for (const [k, v] of Object.entries(EXAMPLE[key])) p.content.slots[k] = t(v);
  if (key === 'webinar') {
    p.content.people = { host: { name: 'Alejandra Cadario', role: 'Anfitriona' }, guest1: { name: 'Nombre Invitada', role: 'Su rol' } };
    p.photos = withPhoto ? { host: { photoId: 'example', crop: { x: 0.5, y: 0.45, zoom: 1 } } } : {};
  } else p.photos = withPhoto && slide === 0 ? { main: { photoId: 'example', crop: { x: 0.5, y: 0.5, zoom: 1 } } } : {};
  return p;
}

/** The photo of the catalog: your first own photo, or a drawn example image when there is none. */
function useSamplePhoto(photos) {
  const [sample, setSample] = useState(null);
  const mine = photos.find(p => p.kind === 'self' && p.hasImage && !p.archived);
  useEffect(() => {
    let on = true;
    if (mine) loadPhoto(mine.id).then(x => on && setSample({ ...x, photo: mine, own: true })).catch(() => on && setSample({ ...exampleImage(), photo: { kind: 'self' }, own: false }));
    else setSample({ ...exampleImage(), photo: { kind: 'self' }, own: false });
    return () => { on = false; };
  }, [mine?.id]);
  return sample;
}

/** The catalog: choose the format, then the design. */
function Catalog({ current, create, brand, sample, reference = false }) {
  const [sel, setSel] = useState('pz_ig_post');
  const item = CATALOG.flatMap(g => g.items).find(i => i.id === sel);
  const useBtn = (onClick, label = 'Usar este diseño') => <button className="action-btn btn-primary" disabled={!current} onClick={onClick}>{label}</button>;
  return (
    <div style={{ ...cardSt, padding: 12 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{reference ? 'Catálogo de diseños' : 'Nueva pieza'}</h3>
      <div style={{ ...mutedSt, marginBottom: 8 }}>
        1. Elegí el formato · 2. Elegí el diseño · 3. Editá · 4. Aprobá · 5. Descargá. Los diseños originales no se editan: cada pieza es una copia que guarda su diseño y su versión. Las piezas guardadas conservan su diseño y su tamaño.
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {CATALOG.map(g => (
          <div key={g.group} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ ...mutedSt, minWidth: 110, fontWeight: 600 }}>{g.group}</span>
            {g.items.map(i => <button key={i.id} type="button" role="radio" aria-checked={sel === i.id} style={chipSt(sel === i.id)} onClick={() => setSel(i.id)}>{i.label}</button>)}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        {item?.pizarra && (() => {
          const k = item.pizarra; const f = BRAND_FORMATS[BRAND_TEMPLATES[k].formats[0]];
          return (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={mutedSt}>{BRAND_TEMPLATES[k].description} {f.width} × {f.height}. Identidad Pizarra. Elegí uno de los dos diseños; después podés cambiarlo sin perder tus textos.</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 12 }}>
                {['A', 'B'].map(l => (
                  <div key={l} style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, display: 'grid', gap: 6, alignContent: 'start' }}>
                    <b style={{ fontSize: 14 }}>{l} · {PZ_LAYOUTS[k][l].label}</b>
                    <PizarraSample templateKey={k} layoutKey={l} maxWidth={k === 'pz_youtube' || k === 'pz_li_article' ? 280 : k === 'pz_ig_story' || k === 'pz_tiktok_cover' ? 130 : 150} pages={k === 'pz_ig_carousel' || k === 'pz_li_document' ? 3 : 1} />
                    <div style={mutedSt}>{PZ_LAYOUTS[k][l].hint}</div>
                    <div>{useBtn(() => create(k, l))}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {item?.notebook && (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={mutedSt}>{BRAND_TEMPLATES.cuaderno.description} Se descarga un PDF con texto seleccionable y buscable.</div>
            <NotebookSample maxWidth={130} />
            <div>{useBtn(() => create('cuaderno'), 'Usar esta plantilla')}</div>
          </div>
        )}
        {item?.script && (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={mutedSt}>{BRAND_TEMPLATES.tiktok.description} Es un guion para grabar: no se descarga como imagen.</div>
            <div>{useBtn(() => create('tiktok'), 'Crear guion')}</div>
          </div>
        )}
        {item?.old && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ ...warnSt, padding: 8 }}>Diseño anterior (verde bosque, marfil y mostaza): sigue disponible hasta que tenga su versión Pizarra.</div>
            {[item.id].map(k => {
              const t = BRAND_TEMPLATES[k];
              return (
                <div key={k} style={{ display: 'grid', gap: 6 }}>
                  <b style={{ fontSize: 14 }}>{t.label}</b>
                  <div style={mutedSt}>{t.description}</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {t.formats.map(f => (
                      <div key={f} style={{ flex: `0 1 ${BRAND_FORMATS[f].width > BRAND_FORMATS[f].height ? 300 : 200}px`, minWidth: 0 }}>
                        <div style={{ ...mutedSt, marginBottom: 4 }}>{BRAND_FORMATS[f].label}</div>
                        <PiecePreview piece={sampleOf(k, f, true)} brand={brand} images={sample ? { [k === 'webinar' ? 'host' : 'main']: sample } : {}} when={k === 'webinar' ? EXAMPLE_WHEN : null}
                          maxWidth={BRAND_FORMATS[f].width > BRAND_FORMATS[f].height ? 300 : 200} label={`Ejemplo de ${t.label}`} />
                      </div>
                    ))}
                  </div>
                  <div>{useBtn(() => create(k), 'Usar este diseño')}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyBrand({ ai }) {
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => { let on = true; brandFontsReady().then(() => on && setFontsReady(true)); return () => { on = false; }; }, []);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('pieces');
  const [editing, setEditing] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [msg, setMsg] = useState('');
  const load = () => { setError(''); return ownerCall({ action: 'brandOverview' }).then(setData).catch(e => setError(e.message)); };
  useEffect(() => { load(); }, []);
  const [upgradeAsk, setUpgradeAsk] = useState(false);
  const sample = useSamplePhoto(data?.photos || []);
  const refreshPhotos = () => ownerCall({ action: 'brandListPhotos' }).then(d => setData(x => ({ ...x, photos: d.photos }))).catch(e => setError(e.message));

  if (error && !data) return <ErrorState message={error} onRetry={load} />;
  if (!data || !fontsReady) return <LoadingState />;
  const current = data.current;

  const open = async (id) => {
    setMsg('');
    try { const d = await ownerCall({ action: 'brandGetPiece', id }); const t = BRAND_TEMPLATES[d.piece.template]; setEditing(t?.pizarra ? { pizarra: true, piece: d.piece } : t?.document ? { notebook: true, piece: d.piece } : { piece: d.piece, webinar: d.webinar }); } catch (e) { setError(e.message); }
  };
  const create = (key, format = null) => {
    // A document (the PDF notebook) has its own editor: pages, not a canvas
    if (BRAND_TEMPLATES[key].document) { setEditing({ notebook: true, piece: newNotebookPiece(current.id) }); return; }
    // Instagram and LinkedIn (identity A): their own editor, with the design chosen in the catalog
    if (BRAND_TEMPLATES[key].pizarra) { const piece = newPizarraPiece(key, current.id); piece.content.layout = format === 'B' ? 'B' : 'A'; setEditing({ pizarra: true, piece }); return; }
    const piece = newPiece(key, current.id, format);
    // The collection starts with your first own photo, framed from the waist up
    const mine = BRAND_TEMPLATES[key].collection && data.photos.find(p => p.kind === 'self' && p.hasImage && !p.archived);
    if (mine) piece.photos = { self: { photoId: mine.id, crop: { x: 0.5, y: 0.5, zoom: 1 }, frame: { ...SELF_FRAME_DEFAULT } } };
    setEditing({ piece });
  };
  const brandNow = current?.profile || DEFAULT_BRAND_PROFILE;
  const upgradeOne = async (p) => { setMsg(''); try { await ownerCall({ action: 'brandUpgradePiece', id: p.id }); setMsg(`"${p.title || 'Pieza'}" usa el estilo aprobado. Tus textos y fotos no cambiaron.`); load(); } catch (e) { setError(e.message); } };
  const upgradeDrafts = async () => { setMsg(''); setUpgradeAsk(false); try { const d = await ownerCall({ action: 'brandUpgradeDrafts' }); setMsg(`${d.count} borrador${d.count === 1 ? '' : 'es'} actualizado${d.count === 1 ? '' : 's'} al estilo aprobado. Textos y fotos sin cambios.`); load(); } catch (e) { setError(e.message); } };
  const archive = async (p, archived) => { try { await ownerCall({ action: 'brandArchivePiece', id: p.id, archived }); load(); } catch (e) { setError(e.message); } };
  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"? No se puede deshacer. La plantilla original no cambia.`)) return;
    try { await ownerCall({ action: 'brandDeletePiece', id: p.id }); load(); } catch (e) { setError(e.message); }
  };

  if (editing?.pizarra) {
    return <PizarraEditor key={editing.piece.id || 'new'} initial={editing.piece} photos={data.photos} ai={ai} onClose={() => { setEditing(null); load(); }} />;
  }
  if (editing?.notebook) {
    return <NotebookEditor key={editing.piece.id || 'new'} initial={editing.piece} photos={data.photos} onClose={() => { setEditing(null); load(); }} />;
  }
  if (editing) {
    return (
      <PieceEditor key={editing.piece.id || 'new'} initial={editing} brandVersions={data.versions} currentVersion={current} photos={data.photos} ai={ai}
        onClose={() => { setEditing(null); load(); }} onSaved={() => {}} onPhotosChanged={refreshPhotos} />
    );
  }

  const pieces = data.pieces.filter(p => showArchived || p.status !== 'archived');
  const isOld = (p) => (p.templateVersion || 1) < (BRAND_TEMPLATES[p.template]?.version || 1);
  const oldDrafts = data.pieces.filter(p => p.status === 'draft' && isOld(p));
  return (
    <div>
      <ReturnToPlan />
      <Notice icon="🌿" tone="private">
        <b>Mi marca · Alejandra Cadario.</b> Las piezas nuevas de Instagram, LinkedIn (también la portada de artículo), YouTube y la portada de TikTok, y los cuadernos PDF, usan la identidad Pizarra (dirección A). La invitación a webinar conserva el diseño anterior hasta su reemplazo, y cada pieza guardada conserva su diseño. Se aplican solo a tus piezas: Oike no cambia.
        La IA propone y tú apruebas. Nada se publica ni se programa: exportas el PNG y copias el texto.
      </Notice>
      {!current && (
        <div style={{ ...warnSt, padding: 12 }}>
          Para crear piezas, guarda primero tu perfil de marca como versión 1 (ya trae la identidad aprobada). <button className="action-btn btn-primary" style={{ marginLeft: 6 }} onClick={() => setTab('profile')}>Ir al perfil</button>
        </div>
      )}
      <SubTabs tabs={[['pieces', 'Mis piezas'], ['templates', 'Plantillas originales'], ['profile', 'Perfil de marca'], ['photos', 'Fotos']]} value={tab} onChange={(t) => { setTab(t); setMsg(''); }} />
      {error && <div role="alert" style={{ color: '#B4362C', fontSize: 13, margin: '6px 0' }}>{error}</div>}
      {msg && <div role="status" style={{ color: '#1E7B4F', fontSize: 13, margin: '6px 0' }}>{msg}</div>}

      {tab === 'pieces' && (
        <div>
          <Catalog current={current} create={create} brand={brandNow} sample={sample} />
          {oldDrafts.length > 0 && (
            <div style={{ ...warnSt, padding: 10 }}>
              {oldDrafts.length === 1 ? 'Un borrador usa' : `${oldDrafts.length} borradores usan`} el estilo anterior. Actualizar cambia solo el estilo (verde bosque, marfil y mostaza): tus textos, fotos y recortes quedan igual. Las piezas aprobadas se actualizan una por una.
              <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {!upgradeAsk ? <button className="action-btn" onClick={() => setUpgradeAsk(true)}>Actualizar {oldDrafts.length === 1 ? 'el borrador' : `los ${oldDrafts.length} borradores`} al estilo aprobado</button> : (
                  <>
                    <button className="action-btn btn-primary" onClick={upgradeDrafts}>Sí, actualizar {oldDrafts.length === 1 ? 'el borrador' : `los ${oldDrafts.length}`}</button>
                    <button className="action-btn" onClick={() => setUpgradeAsk(false)}>Cancelar</button>
                  </>
                )}
              </div>
            </div>
          )}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, margin: '4px 0 10px' }}><input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Mostrar archivadas</label>
          {!pieces.length ? <EmptyState title="Todavía no hay piezas">Elige una plantilla para crear la primera. Cada pieza es una copia editable: la plantilla original no cambia.</EmptyState> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {pieces.map(p => {
                const t = BRAND_TEMPLATES[p.template];
                return (
                  <div key={p.id} style={{ ...cardSt, marginBottom: 0, padding: 12, display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                      <button type="button" onClick={() => open(p.id)} style={{ font: 'inherit', fontWeight: 700, fontSize: 15, background: 'none', border: 'none', padding: 0, color: 'var(--globant-text)', cursor: 'pointer', textAlign: 'left' }}>{p.title || 'Pieza sin título'}</button>
                      {p.headline && <div style={{ ...mutedSt, overflowWrap: 'anywhere' }}>{p.headline}</div>}
                      <div style={{ ...mutedSt, marginTop: 2 }}>Desde la plantilla original {t?.label || p.template} (v{p.templateVersion}) · {BRAND_FORMATS[p.format]?.label} · marca v{p.brandVersion}{p.updatedAt ? ` · ${fmtDate(p.updatedAt)}` : ''}{p.exportedAt ? ` · exportada ${fmtDate(p.exportedAt)}` : ''}</div>
                      {p.eventChanged && <div style={{ fontSize: 12, color: '#9A6212', marginTop: 2 }}>⚠ El evento cambió: actualiza esta invitación.</div>}
                      {p.hasAiDraft && <div style={mutedSt}>Con propuesta de IA guardada aparte.</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {isOld(p) && <span style={{ ...badgeSt(false), color: '#7A4D0E' }}>Estilo anterior</span>}
                      {isOld(p) && p.status !== 'archived' && <button className="action-btn" onClick={() => upgradeOne(p)}>Actualizar al estilo aprobado</button>}
                      <span style={badgeSt(p.status === 'approved')}>{STATUS[p.status] || p.status}</span>
                      <button className="action-btn" onClick={() => open(p.id)}>Abrir</button>
                      <button className="action-btn" onClick={() => archive(p, p.status !== 'archived')}>{p.status === 'archived' ? 'Recuperar' : 'Archivar'}</button>
                      <button className="action-btn btn-danger" onClick={() => del(p)}>Eliminar</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'templates' && <Catalog current={current} create={create} brand={brandNow} sample={sample} reference />}

      {tab === 'profile' && <BrandProfile key={current?.id || 'none'} current={current} versions={data.versions} onSaved={() => load()} />}
      {tab === 'photos' && <BrandPhotos photos={data.photos} onChange={refreshPhotos} />}
    </div>
  );
}

/** Came from a piece of the weekly plan: back to the same piece, with everything that was prepared. */
function ReturnToPlan() {
  let r = null;
  try { r = JSON.parse(sessionStorage.getItem('oike_content_plan_return') || 'null'); } catch { r = null; }
  if (!r?.week) return null;
  return (
    <div style={{ ...warnSt, padding: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <span>Viniste desde el plan semanal. Lo preparado quedó guardado.</span>
      <button className="action-btn btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'contentlab', planWeek: r.week, itemKey: r.itemKey, pieceId: r.pieceId } }))}>Volver al plan semanal</button>
    </div>
  );
}

